// @flow

import * as React from 'react';
import { Map } from 'immutable';
import connect from '@active/awe-ui-core/lib/hoc/connect';
import { bindActionCreators } from 'redux';
import PageBody from 'components/PageBody/PageBody';
import Container from 'components/Container/Container';
import Maybe, { maybe } from '@active/awe-ui-core/lib/fp/Maybe';
import isEqual from 'lodash/isEqual';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import type {
  Action, Participant as ParticipantEntity, Match, Person, L10n, Event as EventType, ParticipantPhotos, ImageItem,
} from 'type/type';
import { clearWhiteLabel } from 'modules/whiteLabel/duck';
import { isWhiteLabelReady } from 'modules/whiteLabel/selector';
import HeaderBar from 'scenes/Event/HeaderBar/HeaderBar';
import prop from 'lodash/fp/prop';
import compose from 'lodash/fp/compose';
import last from 'lodash/fp/last';
import propOr from 'lodash/fp/propOr';
import injectL10n from 'react-aaui/lib/shared/injectL10n';
import 'scenes/Event/duck/epics';
import { getPortalPath } from 'modules/app/selector';
import appendPortalPath from 'services/router/appendPortalPath';
import L10nMessage from 'react-aaui/lib/shared/L10nMessage';
import { isKioskMode } from 'services/kiosk/kiosk';
import { parse } from 'query-string';

import ImageGallery from './ImageGallery/ImageGallery';
import ParticipantHeader from './ParticipantHeader/ParticipantHeader';
import ParticipantResultsTab from './ParticipantResultsTab/ParticipantResultsTab';
import './Participant.scss';
import EventHeader from './EventHeader/EventHeader';
import {
  getParticipant,
  isLoadingParticipant,
  isLiveResult,
  isFetchParticipantFailed,
  isTriathlonResult,
  getEvent,
  getParticipantHeader,
  getShowGallery,
  getParticipantPhoto,
  isLoadingParticipantPhoto,
  getParticipantExternalId,
} from './duck/selector';
import {
  mount, fetchParticipant, setShowGallery, fetchParticipantPhoto, updateParticipantPhotos,
} from './duck/duck';
import './duck/epics';

type Props = {
  isLoading: boolean,
  hasParticipant: boolean,
  participants: [ParticipantEntity],
  mount: (applicationName: string) => Action,
  fetchParticipant: (participantId: string) => Action,
  match: Match,
  isLive: boolean,
  clearWhiteLabel: () => void,
  isFetchDataFailed: boolean,
  isTriathlon: boolean,
  person: Person,
  l10n: L10n,
  windowLocationOrigin: string,
  event?: ?EventType,
  showGallery: boolean,
  loadingParticipantPhoto: Boolean,
  participantPhotos: ParticipantPhotos,
  externalId: string,
  fetchParticipantPhoto: (
    participantId: string,
    externalId: string,
    lastId: string,
    prevImages: Array<ImageItem>
  ) => void,
  updateParticipantPhotos: (id: string, participantCustomFields: ?{
    photoURL: ParticipantPhotos,
    certificateURL: string,
    videoURL: string
  }) => void,
  setShowGallery: (val: boolean) => void
}

const INTERVAL = 10000;
const PENDING_INTERVAL = 2000;

export class Participant extends React.Component<Props> {
  timer: TimeoutID

  spinnerTimer: TimeoutID

  isFirstEnter: boolean

  constructor(props: Props) {
    super(props);
    this.isFirstEnter = true;
  }

  componentDidMount() {
    const pid = this.props.match.params.id;
    const pNumber = parse(prop('props.location.search')(this)).pn;
    this.props.updateParticipantPhotos({
      id: pid,
      participantCustomFields: null,
    });
    this.props.mount(pNumber || pid);
    this.createTimer();
  }

  createTimer = () => {
    this.props.fetchParticipant(this.props.match.params.id);
    this.timer = setTimeout(() => {
      this.isFirstEnter = false;
      this.createTimer();
    }, INTERVAL);
  }

  shouldComponentUpdate(
    {
      participants, isLoading, isLive, hasParticipant, isFetchDataFailed, showGallery, loadingParticipantPhoto,
    }: { participants: [ParticipantEntity], isLoading: boolean, isLive: boolean,
        hasParticipant: boolean, isFetchDataFailed: boolean, showGallery: boolean, loadingParticipantPhoto: boolean }) {
    const shouldUpdate = !isLoading && !isEqual(this.props.participants, participants);
    if (isFetchDataFailed || (hasParticipant && !isLive)) {
      clearTimeout(this.timer);
    }
    if (shouldUpdate && document.getElementsByClassName('participant-spinner')[0]) {
      this.handleSpinner();
    }
    if (this.props.showGallery !== showGallery || this.props.loadingParticipantPhoto !== loadingParticipantPhoto) {
      return true;
    }
    if (this.isFirstEnter) {
      return isLoading !== this.props.isLoading;
    }
    return shouldUpdate;
  }

  componentWillUnmount() {
    this.timer && clearTimeout(this.timer);
    this.spinnerTimer && clearTimeout(this.spinnerTimer);
    this.props.clearWhiteLabel();
    this.props.setShowGallery(false);
    this.props.updateParticipantPhotos({
      id: this.props.match.params.id,
      participantCustomFields: null,
    });
  }

  handleSpinner() {
    document.getElementsByClassName('participant-spinner')[0].style.display = 'block';
    document.getElementsByClassName('participant-spinner')[0].style.zIndex = '2000';
    this.spinnerTimer = setTimeout(() => {
      if (document.getElementsByClassName('participant-spinner')[0]) {
        document.getElementsByClassName('participant-spinner')[0].style.display = 'none';
        document.getElementsByClassName('participant-spinner')[0].style.zIndex = '-2000';
      }
    }, PENDING_INTERVAL);
  }

  toggleGallery = (val: boolean) => {
    this.props.setShowGallery(val);
  }

  viewMorePhoto = () => {
    const images = propOr([], 'content')(this.props.participantPhotos);
    const lastId = compose(prop('id'), last)(images);
    this.props.fetchParticipantPhoto(this.props.match.params.id, this.props.externalId, lastId, images);
  }

  render() {
    const {
      isLoading,
      hasParticipant,
      isLive,
      isTriathlon,
      match: { params: { id } },
      event,
      windowLocationOrigin,
      l10n: { formatMessage },
      person,
      portalPath,
      showGallery,
      participantPhotos,
      loadingParticipantPhoto,
    } = this.props;
    const evt = Maybe.of(event);
    const eventName = maybe(null, prop('name'), evt);
    const applicationName = maybe(null, prop('applicationName'), evt);
    const { firstName, lastName } = person || {};
    const sharingPath = appendPortalPath(portalPath, `/participants/${id}`);
    const sharingHref = `${windowLocationOrigin}${sharingPath}`;
    const sharingText = `${formatMessage('global.social.share.view.participant.event', [ firstName, eventName ])}`;
    const emailSubject = `${formatMessage('global.social.share.email.subject', [ firstName ])}`;
    const moreButtonText = `${formatMessage('global.view.more')}`;
    const total = propOr(0, 'total')(participantPhotos);
    const images = propOr([], 'content')(participantPhotos);
    const isKiosk = isKioskMode(window);

    if ((isLoading && this.isFirstEnter) || (loadingParticipantPhoto && !showGallery)) {
      return <PageSpinner />;
    }

    return (
      hasParticipant
        ? <React.Fragment>
          <EventHeader />
          <HeaderBar
            sharingHref={sharingHref}
            sharingText={sharingText}
            emailSubject={emailSubject}
            event={event}
            applicationName={applicationName} />
          {
            showGallery ? (
              <Container>
                <div className="participant-photo-gallery-breadcrumb">
                  <div className="participant-photo-gallery-breadcrumb__name" onClick={() => this.toggleGallery(false)}>
                    {firstName}
                    {lastName}
                  </div>
                  <div>&gt;</div>
                  <div className="participant-photo-gallery-breadcrumb__title">
                    <L10nMessage id="page.participant.photos" />
                  </div>
                </div>
                <div className="participant-photo-gallery-title">
                  <L10nMessage id="page.participant.photos" />
                </div>
                <ImageGallery
                  images={images}
                  moreButtonText={moreButtonText}
                  onViewMore={this.viewMorePhoto}
                  loading={loadingParticipantPhoto}
                  total={total} />
              </Container>
            ) : (
              <React.Fragment>
                <div className="participant-header--wrapper">
                  <Container>
                    <ParticipantHeader toggleGallery={this.toggleGallery} isKioskMode={isKiosk} />
                  </Container>
                </div>
                <PageBody>
                  <ParticipantResultsTab isLive={isLive} isTriathlon={isTriathlon} />
                </PageBody>
              </React.Fragment>
            )
          }
          {
            isLive && <div className="participant-spinner"><PageSpinner /></div>
          }
        </React.Fragment>
        : null
    );
  }
}

const mapState = (state: Map<string, *>) => {
  const isLoading = isLoadingParticipant(state) || !isWhiteLabelReady(state);
  const participants = getParticipant(state);
  return {
    hasParticipant: !isLoading && maybe(false, () => true, participants),
    participants,
    isLoading,
    isLive: isLiveResult(state),
    isTriathlon: isTriathlonResult(state),
    isFetchDataFailed: isFetchParticipantFailed(state),
    event: getEvent(state),
    windowLocationOrigin: window.location.origin,
    person: getParticipantHeader(state) && getParticipantHeader(state).get('person'),
    portalPath: getPortalPath(state),
    showGallery: getShowGallery(state),
    participantPhotos: getParticipantPhoto(state),
    loadingParticipantPhoto: isLoadingParticipantPhoto(state),
    externalId: getParticipantExternalId(state),
  };
};

const mapDispatch = dispatch => bindActionCreators({
  mount, fetchParticipant, clearWhiteLabel, setShowGallery, fetchParticipantPhoto, updateParticipantPhotos,
}, dispatch);

const ParticipantContainer = connect(mapState, mapDispatch)(Participant);

export default injectL10n()(ParticipantContainer);
